import {Component, ElementRef, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ApiService} from '../../api.service';
import {Title} from '@angular/platform-browser';
import {PDFDocument} from 'pdf-lib';
import {SelectReasonModalComponent} from "./select-reason-modal/select-reason-modal.component";
import {ProductStatusEnum} from "../../core/enums/product-status.enum";
import {ReturnsStore} from "../../core/store/returns.state";
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DatePipe, DecimalPipe, UpperCasePipe} from '@angular/common';
import {MatDialog} from "@angular/material/dialog";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {take} from "rxjs";
import {ErrorNotificationModalComponent} from "../error-notification-modal/error-notification-modal.component";
import {HttpErrorResponse} from "@angular/common/http";
import {
	AddRequestBodyModel,
	ClientReturnsDataModel,
	OrderItemsDataModel
} from "../../core/models/client-returns-data.model";
import {SelectQuantityModalComponent} from "./select-quantity-modal/select-quantity-modal.component";
import {buildReturnFormPdfBytes} from "../../core/utils/return-form-pdf.util";
import {
	base64ToUint8Array,
	buildReturnFormLabels
} from "../../core/utils/return-document.util";
import {downloadPdf} from "../../core/utils/pdf-download.util";

declare const $: any;

@Component({
	selector: 'app-order-view',
	templateUrl: './order-view.component.html',
	styleUrls: ['./order-view.component.scss'],
	standalone: true,
	imports: [ReactiveFormsModule, FormsModule, UpperCasePipe, DecimalPipe, TranslateModule, DatePipe]
})
export class OrderViewComponent implements OnInit {
	private _store = inject(ReturnsStore);
	private _apiService = inject(ApiService);
	private _route = inject(ActivatedRoute);
	private _router = inject(Router);
	private _titleService = inject(Title);
	private _dialog = inject(MatDialog);

	form = this._store.getCurrentState().hasForm;
	productBrand = this._route.snapshot.params.brand;
	items: OrderItemsDataModel[] = [];
	client: any = {};
	order_header: ClientReturnsDataModel | undefined;
	data: object = {};
	email: string = '';
	order_num: string = '';
	totalQuantity: number = 0;
	returns: any;
	request: any;
	general_data: any;
	returnReasons = ['-', ...this._store.getCurrentState().returnReasons];
	invalidForm = true;
	itemsToBeReturned: any[] = [];
	productStatusEnum = ProductStatusEnum;
	disableDownloadButton = false;
	readonly isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
	translateService = inject(TranslateService);
	elRef = inject<any>(ElementRef);

	ngOnInit(): void {
		this._showPageLoader();
		this._setTitle();
		this._getQueryParams();
	}

	checked_row(item: any, itemIndex: number, event?: any) {
		if (event) {
			if (item.return_reason === '-' || item.return_reason === undefined) {
				this.onSelectReasonModal(item, itemIndex);
				return;
			}
			event.stopPropagation();
		}
		const index = this.itemsToBeReturned.indexOf(item);
		if (index > -1) {
			this.itemsToBeReturned.splice(index, 1);
			this.checkFormValidity();
			this.items[itemIndex].checked = false;
			return;
		}
		this.items[itemIndex].checked = true;
		this.items[itemIndex].selectedQuantity = 1;
		this.itemsToBeReturned.push(item);
		this.checkFormValidity();
	}

	checkFormValidity() {
		if (!this.itemsToBeReturned.length) {
			this.invalidForm = true;
			return;
		}
		this.invalidForm = this.itemsToBeReturned.some((item: {
			return_reason: string;
			damage_description: string
		}) => {
			return item.return_reason === '-' ||
				item.return_reason === undefined;
		});
	}

	onSelectReasonModal(item: any, index: number) {
		if (item.status === this.productStatusEnum.NOT_FULFILLED || item.status === this.productStatusEnum.CANCELLED) {
			return;
		}
		const dialogRef = this._dialog.open(SelectReasonModalComponent, {data: this.items[index].return_reason});
		dialogRef.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.items[index].return_reason = result;
			this.checked_row(item, index);
		});
	}

	onSelectQuantity(item: any, index: number) {
		const dialogRef = this._dialog.open(SelectQuantityModalComponent, {data: this.items[index]});
		dialogRef.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.items[index].selectedQuantity = result;
		})
	}

	cancel() {
		this._redirectToLogin();
	}

	async addReturn() {
		if (!this.itemsToBeReturned.length) {
			return;
		}

		this._showReturnProcessLoader();
		const addRequestBody: AddRequestBodyModel = {
			order_id: this.order_header?.order_id,
			order_number: this.order_header?.order_number,
			order_source: this._store.getCurrentState().source,
			language: this.translateService.currentLang,
			company: this.order_header?.company,
			client: {
				firstname: this.order_header?.client.firstname || '',
				lastname: this.order_header?.client.lastname || '',
				salutation: this.order_header?.client.salutation || '',
				email: this.client.email,
				telephone: this.order_header?.client.telephone || '',
				address: {
					city: this.order_header?.client.address.city || '',
					country: this.order_header?.client.address.country || '',
					postcode: this.order_header?.client.address.postcode || '',
					street: this.order_header?.client.address.street || '',
					number: this.order_header?.client.address.number || ''
				}
			},
			order_items: this.itemsToBeReturned.map(item => {
				return {
					item_ref: item.item_ref,
					sku: item.sku,
					price: item.selling_price,
					quantity: item.selectedQuantity,
					return_reason: item.return_reason
				}
			})
		}

		this.request = addRequestBody;

		this._apiService.add_request(addRequestBody).subscribe(async (response) => {
			if (!this.order_header?.client) {
				this._dialog.open(ErrorNotificationModalComponent, {
					data: {
						title: this.translateService.instant('returnsViewPage.error'),
						message: this.translateService.instant('returnsViewPage.labelGenerateError')
					}
				});
				this._hideReturnProcessLoader();
				return;
			}
			const itemsForPdf = this.itemsToBeReturned.map((item) => ({
				article_code: item.article_code,
				sku: item.sku,
				quantity: item.selectedQuantity ?? item.quantity ?? 1,
				return_reason: item.return_reason,
				return_reason_description: item.return_reason_description,
				damage_description: item.damage_description
			}));
			this.general_data.return_no = response.returnNumber;
			this.itemsToBeReturned = [];

			try {
				const formPdfBytes = await buildReturnFormPdfBytes({
					brand: this.productBrand,
					returnNumber: response.returnNumber,
					orderNumber: this.general_data.order_number,
					returnDate: this._formatDateAsGermanDateFormat(this.order_header?.order_date),
					client: this.order_header.client,
					items: itemsForPdf,
					labels: buildReturnFormLabels(this.translateService.instant.bind(this.translateService))
				});
				const backendPdfBytes = base64ToUint8Array(response.label);
				const mergedPdf = await PDFDocument.create();
				for (const document of [formPdfBytes, backendPdfBytes]) {
					let pdfDoc1;
					try {
						pdfDoc1 = await PDFDocument.load(document);
					} catch (error: any) {
						this._dialog.open(ErrorNotificationModalComponent, {
							data: {
								title: this.translateService.instant('returnsViewPage.error'),
								message: error?.message ? error?.message : this.translateService.instant('returnsViewPage.labelGenerateError')
							}
						})
							.afterClosed()
							.pipe(take(1))
							.subscribe();
						return;
					}

					const copiedPages = await mergedPdf.copyPages(pdfDoc1, pdfDoc1.getPageIndices());
					copiedPages.forEach((page) => mergedPdf.addPage(page));
				}

				const pdfBytes = await mergedPdf.save();
				const pdfBuffer = pdfBytes.buffer as ArrayBuffer;
				const blob = new Blob([pdfBuffer], {type: 'application/pdf'});
				this._apiService.sendEmail({
					file: blob,
					name: `${this.client.firstname} ${this.client.lastname}`,
					orderNumber: this.general_data.order_number,
					emailTo: this.email
				}).subscribe();
				downloadPdf(pdfBytes, this._apiService.generateDownloadFileName(this.general_data.order_number));
				this._refreshOrderData();
			} finally {
				this._hideReturnProcessLoader();
				this.ngOnInit();
			}

		}, (error: HttpErrorResponse) => {
			if (error.status === 503) {
				this._hideReturnProcessLoader();
				return;
			}
			const handleErrorMessage = (error: string) => {
				if (error && error.includes('201')) {
					const handleMissingData = (error: string) => {
						if (error.includes('Name 1')) {
							return 'First Name';
						}
						if (error.includes('Name 2')) {
							return 'Last Name';
						}
						return error.split('Feld \\"')[1].split('\\".')[0];
					}

					const missingData = handleMissingData(error);
					return this.translateService.instant('returnsViewPage.labelGenerateError201', {missingData});
				}
				if (error) {
					return error;
				}
				return this.translateService.instant('returnsViewPage.labelGenerateError');
			}
			this._dialog.open(ErrorNotificationModalComponent, {
				data: {
					title: this.translateService.instant('returnsViewPage.error'),
					message: handleErrorMessage(error?.error?.message)
				}
			})
			this._hideReturnProcessLoader();
		});
	}

	async your_return_view(returnNo: number) {
		this._showPageLoader();
		await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
		let param_email = this._buildEmailParam();
		let param_order = this._buildOrderNumberParam();
		await this._router.navigateByUrl(`your_return/${this._route.snapshot.params.brand}?lang=${this.translateService.currentLang}&email=${param_email}&order=${param_order}&formonly=${this.form}&return_no=${returnNo}`);
	}

	onSelect(event: any, index: number): void {
		this.items[index].return_reason = event.target.value;
		this.checkFormValidity();
	}

	onQuantityChange(event: any, index: number): void {
		this.items[index].selectedQuantity = event.target.value;
	}

	private _showReturnProcessLoader() {
		this.disableDownloadButton = true;
		(document.getElementById('process_loading') as HTMLElement).classList.remove('no-display');
	}

	private _hideReturnProcessLoader() {
		this.disableDownloadButton = false;
		this.checkFormValidity();
		(document.getElementById('process_loading') as HTMLElement).classList.add('no-display');
	}

	private _buildOrderNumberParam() {
		return btoa(this.order_num);
	}

	private _buildEmailParam() {
		return btoa(this.email);
	}

	private _formatDateAsGermanDateFormat(date: any) {
		if (date == null) {
			return '';
		}
		const s = String(date).trim();
		// If format is DD-MM-YYYY, convert to DD.MM.YYYY
		if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
			return s.replace(/-/g, '.');
		}
		// Fallback: try to parse as Date and format as DD.MM.YYYY
		const d = new Date(s);
		if (!isNaN(d.getTime())) {
			const dd = String(d.getDate()).padStart(2, '0');
			const mm = String(d.getMonth() + 1).padStart(2, '0');
			const yyyy = d.getFullYear();
			return `${dd}.${mm}.${yyyy}`;
		}
		// If parsing fails, return the original string
		return s;
	}

	private _getData() {
		this._apiService.read_data(this.email, this.order_num).subscribe(
			(data) => {
				if (!data) {
					this.noOrderItemAction();
					return;
				}
				this._setClient(data);
				this.items = data.order_items;

				requestAnimationFrame(() => {
					const root = this.elRef.nativeElement.querySelector('.order-list');
					if (root) {
						root.style.webkitTransform = 'translate3d(0,0,0)';
						// remove shortly after
						setTimeout(() => root.style.webkitTransform = '', 50);
					}
				});
				this.totalQuantity = data.order_items.reduce((total, item) => total + item.quantity, 0);
				this.order_header = data;
				this._dismissLoader();
				this.general_data = data;
				this.order_header.order_date = this.order_header.order_date.split('-').join('.');
			},
			(error) => {
				this._dismissLoader();
				if (error?.status === 503) {
					return;
				}
				this._redirectToLogin();
			}
		);
	}

	private _setClient(obj: any) {
		this.client = obj.client;
	}

	private _dismissLoader() {
		(document.getElementById('page_loader') as HTMLElement).classList.add('no-display');
	}

	private _showPageLoader() {
		(document.getElementById('page_loader') as HTMLElement).classList.remove('no-display');
	}

	private _redirectToLogin() {
		this._router.navigateByUrl(`/check-order/${this._route.snapshot.params.brand}?lang=${this.translateService.currentLang}&formonly=${this.form}`);
	}

	private noOrderItemAction() {
		$('#no_order_item').modal('show');
		this._dismissLoader();
	}

	private _getReturns(orderNumber: string) {
		this._apiService.get_returns(orderNumber).subscribe(
			(data) => {
				if (data.length > 0) {
					this.returns = data;
					for (let i = 0; i < this.returns.length; i++) {
						this.returns[i].order_date = this._formatDateAsGermanDateFormat(this.returns[i].order_date);
					}
					this.returns = this.returns.sort((a: { return_no: number; }, b: {
						return_no: number;
					}) => b.return_no - a.return_no);
				}
			},
			() => {
			}
		);
	}

	private _getQueryParams() {
		this._route.queryParams.subscribe((params) => {
			if (!params) {
				this._dismissLoader();
				return;
			}
			this.email = decodeURIComponent(atob(params.email));
			this.order_num = decodeURIComponent(atob(params.order));
			this._getData();
			this._getReturns(this.order_num);
		});
	}

	private _setTitle() {
		this._titleService.setTitle('AstorMueller | Order items');
	}

	private _refreshOrderData() {
		this._showPageLoader();
		this._getData();
		this._getReturns(this.order_num);
	}

}
